/**
 * 第五階段（信義套件）：台北101、信義區、象山與臺北車站一帶。
 * 標示「英文練習」的句子在所有語言中都刻意保留英文。
 */
import type { Messages } from './en';

export const zhTWXinyi: Pick<Messages, 'xinyi'> = {
  xinyi: {
    mapNames: {} as Record<string, string>,
    zones: {
      xy_xiangshan: '象山',
      xy_viewpoint: '象山觀景台',
      xy_sysHall: '國父紀念館',
      xy_avenue: '忠孝東路',
      xy_arcades: '騎樓商店街',
      xy_stationFront: '車站前',
      xy_village: '四四南村',
      xy_xinyi: '信義商圈',
    },
    objects: {
      xy_taxi: '計程車招呼站',
      xy_sysHall: '國父紀念館',
      xy_cuihu: '翠湖',
      xy_mrtSys: '捷運・國父紀念館站',
      xy_mall101: '台北101購物中心',
      xy_love: '「愛」字雕塑',
      xy_fireworks: '跨年煙火看板',
      xy_conv: '好鄰居超商',
      xy_tea: '泡泡熊手搖飲',
      xy_pharmacy: '藥局',
      xy_trash: '垃圾車停靠點',
      xy_shrine: '土地公廟',
      xy_village: '四四南村',
      xy_bagel: '好丘咖啡',
      xy_market1: '市集攤位：手工皂和提籃',
      xy_market2: '市集攤位：小盆栽',
      xy_market3: '市集攤位：明信片',
      xy_dept: '銀河百貨',
      xy_books: '書林書店',
      xy_screen: '月光廣場大螢幕',
      xy_stage: '街頭藝人表演區',
      xy_mrt101: '捷運・台北101／世貿站',
      xy_trailhead: '象山步道登山口',
      xy_rocks: '六巨石',
      xy_viewpoint: '象山觀景台',
      xy_easycard: '悠遊卡加值機',
      xy_priority: '博愛座',
    },
    npc: {
      xy_office1: '上班族',
      xy_office2: '上班族',
      xy_tourDad: '觀光客爸爸',
      xy_tourKid: '觀光客小女孩',
      xy_grandma: '林奶奶',
      xy_hiker: '登山客',
      xy_clerk: '書店店員',
      xy_teaSeller: '飲料店店員',
      xy_trashLady: '鄰居阿姨',
      xy_vendor: '市集手作人',
      xy_taichi: '打太極的爺爺',
      xy_taxiDriver: '計程車司機',
      xy_performer: '氣球藝人',
    },
    items: {} as Record<string, { name: string; desc: string }>,

    common: {
      quiz: '📝 英文小測驗',
      right: '✅ 答對了！',
      wrong: '❌ 不太對喔，再試一次！',
      back: '等一下再說',
    },
    welcome: '歡迎來到臺北！往東走、經過國父紀念館，就是台北101。象山就在它後面。',

    sysHall: {
      text: '國父紀念館是為了紀念中華民國國父孫中山先生而建的。看看那金黃色的屋頂：屋角像鳥的翅膀一樣往上翹。館內有儀隊站在巨大的銅像旁一動也不動，每個小時交接一次。',
      watch: '💂 看儀隊交接',
      watched: '喀、喀！儀隊踏著整齊的步伐前進，然後站得像雕像一樣挺。',
      q: '英文練習：“What colour is the roof of the Memorial Hall?”', // English practice
      a: ['It is yellow.', 'It is green.', 'It is black.'], // English practice
      ok: '✅ 對！黃色的琉璃瓦在陽光下閃閃發亮。',
    },
    cuihu: {
      text: '「翠湖」就是「翠綠色的湖」。紅色、白色和金色的錦鯉在荷葉之間游來游去，烏龜在石頭上曬太陽。',
      look: '🐟 看魚',
      say: 'Look! The fish are so colourful!', // English practice
      looked: '一條大大的橘色錦鯉對你張開嘴巴。啵！',
    },
    mall: {
      text: '台北101購物中心在大樓的一到五樓：有服飾店、超市，還有很大的美食街。',
      q: '英文練習：你問：“Excuse me, where is the food court?” 警衛回答了。哪一個回答說得通？', // English practice
      a: ['It is on the basement level, B1. Take the escalator down.', 'It is in my pocket.', 'It is on the moon, next to the cat.'], // English practice
      ok: '✅ 沒錯！臺北的美食街通常在地下樓層。',
      browse: '👀 逛逛櫥窗',
      browsed: '亮晶晶的店家，還有涼涼的冷氣。只看不買……也很好玩！',
    },
    love: {
      text: '廣場上有一座巨大的紅色「愛」字雕塑。大家都會在這裡拍照！',
      ask: '📸 請別人幫你拍照',
      howAsk: '要怎麼有禮貌地拜託別人？',
      ok: 'Excuse me, could you take a photo of me, please?', // English practice
      bad: ['Photo. Now. You.', 'Give me your camera.'], // English practice
      done: '“Sure! Say cheese!” 喀嚓！和大大的紅色「愛」字拍了一張好可愛的照片。',
    },
    fireworks: {
      text: '每年跨年夜的午夜十二點，台北101的每一層樓都會噴出煙火，持續大約五分鐘。幾十萬人在信義區一起倒數。',
      q: '英文練習：“When do the fireworks start?”', // English practice
      a: ['At midnight, when the new year begins.', 'At lunchtime on Tuesdays.', 'Every morning at seven.'], // English practice
      ok: '✅ 對！十、九、八……新年快樂！',
      countdown: 'Ten, nine, eight, seven, six, five, four, three, two, one… Happy New Year!', // English practice
    },
    conv: {
      text: '叮咚！好鄰居超商的開門鈴聲。台灣的便利商店可以買零食、繳費、印文件，而且二十四小時都開著。',
      snack: '🥚 買茶葉蛋和水',
      bag: '🛍️「需要袋子嗎？」',
      food: { vendor: 'Welcome! Anything else?', ok: 'One tea egg and a bottle of water, please.', bad: ['Egg. Water. Go.', 'I want the whole shop for free.'], done: '一顆熱呼呼、飄著醬油和八角香的茶葉蛋，配上一瓶冰水。太完美了！', price: 25 }, // English practice
      bagQ: '英文練習：店員問：“Do you need a bag?” 你自己有帶袋子。你要怎麼說？', // English practice
      bagA: ['No, thanks. I have one.', 'Bag is me.', 'Yes, I need ten bags for one egg.'], // English practice
      bagOk: '✅ 很棒！在台灣塑膠袋要另外付錢，所以大家都會自備購物袋。',
    },
    tea: {
      text: '泡泡熊：珍珠奶茶是台灣發明的喔！你可以選甜度（全糖、半糖、微糖、無糖）和冰塊（少冰、去冰）。',
      menu: '你想喝什麼？',
      milk: '🧋 珍珠奶茶',
      green: '🍵 茉莉綠茶',
      milkOrder: { vendor: 'One pearl milk tea! How much sugar and ice?', ok: 'Half sugar, less ice, please.', bad: ['Sugar sugar ice ice!', 'Give me all the sugar and no cup.'], done: '搖一搖，啵！吸管插下去。Q彈的珍珠配上濃濃的奶茶，甜度剛剛好！', price: 50 }, // English practice
      greenOrder: { vendor: 'Jasmine green tea! Sugar and ice level?', ok: 'No sugar and no ice, please.', bad: ['Tea. Cold. Sugar? Maybe.', 'I want a hot ice tea.'], done: '清爽、有花香又新鮮。是在地人的最愛！', price: 35 }, // English practice
    },
    pharmacy: {
      text: '藥局的英文是 pharmacy。藥師可以幫忙處理感冒、頭痛這類小問題。',
      q: '英文練習：你頭痛。你要怎麼問？', // English practice
      a: ['Excuse me, do you have something for a headache?', 'My head is a banana.', 'Give me all the medicine.'], // English practice
      ok: '✅ 太好了。藥師笑著說：“Drink water and get some rest!”',
    },
    trash: {
      text: '臺北街上的垃圾桶很少。每天傍晚，黃色垃圾車會一邊播音樂一邊開過來——現在多半是巴達傑芙斯卡的〈少女的祈禱〉（有些地方還是放貝多芬的〈給愛麗絲〉）——大家就提著垃圾袋跑出來。資源回收要丟到後面另一台車。',
      here: '🎵 垃圾車來了！鄰居們提著垃圾袋趕快跑出來。',
      notYet: '垃圾車大約晚上八點會來這裡。',
      q: '英文練習：你提著垃圾袋出門時要說什麼？', // English practice
      a: ['I am taking out the trash!', 'I am taking out the dog’s homework!', 'I am eating the trash!'], // English practice
      ok: '✅ 對！也別忘了做好資源回收分類。',
      toast: '🎵 噹噹噹噹……〈少女的祈禱〉！垃圾車從街上開過來了。',
    },
    shrine: {
      text: '這是一間小小的土地公廟，土地公會保佑整個社區。大家會拿香拜拜，再放上水果、茶或餅乾。',
      bow: '🙏 拜三下',
      wish: 'Please keep my family safe and healthy.', // English practice
      bowed: '你拜了三下。香煙裊裊升起，飄進榕樹裡。',
    },
    village: {
      text: '四四南村建於1948年，是給第四十四兵工廠員工和家眷住的地方。低矮的灰磚房子現在變成了小小的展覽館、商店和咖啡廳——就在台北101的正下方：新舊並存。',
      q: '英文練習：“Who lived in these houses in the old days?”', // English practice
      a: ['Military families.', 'Pirates.', 'Astronauts.'], // English practice
      ok: '✅ 對！以前台灣各地都有眷村。',
      market: '每個星期日，草地上會變成「簡單市集」：有手作小物、植物和點心。',
      noMarket: '簡單市集只在星期日舉辦。到時候再來吧！',
    },
    bagel: { vendor: 'Hi! Our bagels are fresh from the oven. What would you like?', ok: 'Could I have a sesame bagel and a cup of tea, please?', bad: ['Bagel! Hurry!', 'I want a bagel made of tea.'], done: '在老眷村房子裡，吃著溫熱有嚼勁的芝麻貝果、喝著茶。好溫馨！', price: 45 }, // English practice
    market: {
      ask: 'How much is this, please?', // English practice
      stalls: {
        xy_market1: { text: '有葡萄柚香味的手工皂，還有編織的小提籃。', answer: 'It is thirty dollars.', price: 30 }, // English practice
        xy_market2: { text: '種在茶杯裡的小多肉，還有用舊鐵罐做的花盆。', answer: 'It is twenty dollars.', price: 20 }, // English practice
        xy_market3: { text: '手繪的明信片：台北101、捷運和街貓。', answer: 'It is fifteen dollars.', price: 15 }, // English practice
      },
      buy: (n: number) => `🛍️ 買下來（${n} 枚金幣）`,
      bought: '你買到一件手作小物。手作人笑咪咪地用舊報紙幫你包起來。',
      poor: '你的金幣不夠。',
    },
    dept: {
      text: '銀河百貨：十層樓的商店，用空橋和隔壁的百貨公司連在一起。每年秋天，每家百貨都會有超大的週年慶。',
      q: '英文練習：你喜歡一件T恤。你要問店員什麼？', // English practice
      a: ['Excuse me, can I try this on?', 'T-shirt! Me! Now!', 'How old is this shirt?'], // English practice
      ok: '✅ “Of course! The fitting room is over there.”',
    },
    books: {
      text: '書林書店：一間很有名的書店，大家會坐在地上看好幾個小時的書。好幾層樓的書、音樂、文具，還有咖啡廳。',
      read: '📖 看一會兒書',
      readDone: '你看了一本英文繪本，講一隻貓咪搭捷運的故事。時間過得好快！',
      askClerk: '🗣️ 問問英文書在哪裡',
      clerkSays: '店員說：“English books? They are on the second floor, next to the stairs.”', // English practice
      q: '英文書在哪裡？',
      a: ['On the second floor, next to the stairs.', 'In the basement, next to the café.', 'On the roof.'], // English practice
      ok: '✅ 聽得很仔細！上樓去吧。',
      closed: '書林書店現在沒開。早上十點才開門。',
    },
    screen: {
      text: '月光廣場的大螢幕會播廣告、音樂影片和城市小知識。',
      q: '英文練習：螢幕上問：“How tall is Taipei 101?”', // English practice
      a: ['It is 508 metres tall.', 'It is 50 metres tall.', 'It is 5,000 metres tall.'], // English practice
      ok: '✅ 對！從2004年到2010年，它是全世界最高的大樓。',
    },
    stage: {
      text: '這裡是街頭表演區。在臺北表演要有街頭藝人證照，他們每天傍晚都在這裡表演。',
      watch: '🎶 看表演',
      watched: '音樂、氣球和掌聲。觀眾都在歡呼！',
      tip: '🪙 打賞 5 枚金幣',
      thanks: 'That was amazing! Thank you!', // English practice
      empty: '現在沒有人在表演。下午或傍晚再來吧。',
    },
    trail: {
      text: '象山步道：爬大約二十分鐘陡峭的石階，就能到看得見台北101的觀景台。記得帶水，累了就休息一下！',
      q: '英文練習：“How long does it take to get to the top?”', // English practice
      a: ['About twenty minutes.', 'About twenty days.', 'Twenty seconds.'], // English practice
      ok: '✅ 對！出發吧：一步一步慢慢來。',
      tired: '呼，好多階梯喔！（−體力）',
    },
    rocks: {
      text: '六巨石：象山山頂附近的六塊超大岩石。登山客會爬上去拍一張有名的照片。',
      climb: '🧗 爬上岩石',
      careful: 'Be careful! Hold on tight.', // English practice
      climbed: '你小心翼翼地爬上去，在頂端揮揮手。整座城市在腳下閃閃發亮！',
    },
    viewpoint: {
      text: '這就是經典的景色：台北101高高聳立在整座城市之上。',
      shoot: '📸 拍照',
      say: 'What a view! Taipei 101 looks so tall from here.', // English practice
      title: '📸 象山',
      caption: '你在象山拍的台北101。',
      tap: '點一下關閉',
    },
    taxi: {
      text: '臺北到處都是黃色計程車。司機問：“Where to?”', // English practice
      to: {
        station: 'To Taipei Main Station, please.',
        tower: 'To Taipei 101, please.',
        village: 'To Four Four South Village, please.',
        trail: 'To the Elephant Mountain trailhead, please.',
      }, // English practice
      price: (n: number) => `${n} 枚金幣`,
      poor: '你需要更多金幣才能搭計程車。',
      ride: '🚕 出發囉！“Please fasten your seat belt.”', // English practice
    },
    easycard: {
      text: '悠遊卡加值機：把卡片放在感應區，選擇金額，再投錢。悠遊卡可以搭捷運、公車、騎YouBike，也能在便利商店使用。',
      top: '💳 幫我的卡加值',
      q: '英文練習：螢幕上寫著：“Please place your card on the reader.” 你要先做什麼？', // English practice
      a: ['I put my card on the reader.', 'I put my card in my mouth.', 'I shout at the machine.'], // English practice
      q2: '英文練習：“How much would you like to add?”', // English practice
      a2: ['One hundred dollars, please.', 'My name is one hundred.', 'Yes, I would like a train.'], // English practice
      ok: '✅ 嗶！加值完成。螢幕上會顯示餘額。',
    },
    priority: {
      text: '深藍色的座位是博愛座。誰都可以坐，但遇到更需要的人就要讓座。',
      q: '英文練習：一位老爺爺上車了。你要說什麼？', // English practice
      a: ['Please, take my seat.', 'This is my seat forever.', 'Go away, please.'], // English practice
      ok: '✅ 你真貼心！他說：“Thank you, young friend.”',
    },

    talk: {
      guideTip: '小提醒：黃色計程車在車站旁邊排班。台北101在東邊，象山就在它後面！',
      office1: [
        '午休時間！中午地下室的美食街人超多的。',
        '我在那邊一棟大樓的35樓上班。我們的電梯可沒有101的那麼快！',
        '英文練習：“I have a meeting at two.” “See you later!”', // English practice
      ],
      office2: [
        '我每天搭捷運。嗶一下悠遊卡就出發！',
        '下班後我喜歡走空橋。完全不用過馬路！',
        '英文練習：“Excuse me, is this seat taken?” “No, go ahead.”', // English practice
      ],
      tourAsk: 'Excuse me! We are lost. How do we get to Taipei 101?', // English practice
      tourQ: '要怎麼幫人指路？',
      tourA: ['Go straight along this road, then look for the tallest tower. You can’t miss it!', 'Taipei 101 is in my bag.', 'Turn left, turn left, turn left, turn left.'], // English practice
      tourOk: '“Thank you so much! You are very kind.” 一家人揮揮手，往東邊走去。',
      tourKid: ['我好喜歡機車！這裡好多喔！', '爸爸說等一下可以喝珍珠奶茶！', '英文練習：“What is your name?” “My name is Lily. Nice to meet you!”'], // English practice
      grandmaShrine: [
        '我每天早上都來拜土地公。祂保佑我們這條街平平安安。',
        '拜三下，像這樣。輕輕的、慢慢的。',
        '英文練習：“Good morning, Grandma!”——跟我說說看！', // English practice
      ],
      grandmaMarket: [
        '哎呀，星期日的市集！我每次都會買一小盆植物。',
        '我是在這樣的眷村長大的。以前鄰居之間什麼都一起分享。',
      ],
      hiker: [
        '加油！山頂的風景值得你爬每一階。',
        '英文練習：“I am out of breath!” “Let’s take a short break.”', // English practice
        '太陽下山的時候101會亮燈。留下來看夜景吧！',
      ],
      clerk: [
        '歡迎光臨書林。想看什麼都可以，慢慢看。',
        '我們的英文書區在二樓，樓梯旁邊。',
        '英文練習：“Could you recommend a good book?”', // English practice
      ],
      trashLady: [
        '快點快點！一聽到〈少女的祈禱〉，就是垃圾車要來了！',
        '垃圾丟黃色的車，回收丟白色的車。',
        '英文練習：“Don’t forget to take out the trash!”', // English practice
      ],
      vendor: [
        '這裡每一樣東西都是我親手做的！',
        '英文練習：“How much is this?” “It is twenty dollars.”', // English practice
      ],
      taichi: [
        '慢慢地……吸氣……吐氣。打太極讓我保持年輕！',
        '我八十二歲了。每天早上都來這裡。',
        '英文練習：“Breathe in. Breathe out. Relax.”', // English practice
      ],
      taxiDriver: [
        '要去哪裡？臺北每一條路我都熟！',
        '英文練習：“Please fasten your seat belt.”', // English practice
      ],
      performer: [
        '送你一個氣球！你要小狗還是長頸鹿？',
        '英文練習：“That was amazing!”——觀眾都會這樣說。', // English practice
      ],
      teaSeller: '歡迎光臨泡泡熊！甜度要多少？冰塊呢？',
    },
  },
};
